import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiZap, FiPlus, FiX, FiCheck, FiClock, FiMail, FiFileText, FiUser,
  FiAlertCircle, FiSettings, FiPlay, FiPause, FiTrash2, FiEdit3,
  FiCalendar, FiMessageSquare, FiUpload, FiDownload, FiShield, FiToggleLeft,
  FiToggleRight, FiRefreshCw, FiActivity, FiTrendingUp, FiChevronRight
} = FiIcons;

const mockAutomations = [
  {
    id: 1,
    name: 'Document Upload Notification',
    description: 'Notify all participants when a new document is uploaded',
    trigger: 'Document Upload',
    actions: ['Send Email', 'In-App Notification'],
    status: 'active',
    executions: 45,
    lastRun: '2024-01-15 14:30:00',
    created: '2024-01-10'
  },
  {
    id: 2,
    name: 'Auto-Approve Lab Results',
    description: 'Automatically approve lab results from verified sources',
    trigger: 'Document Upload',
    actions: ['Set Status to Approved', 'Notify Patient'],
    status: 'active',
    executions: 23,
    lastRun: '2024-01-15 10:20:00',
    created: '2024-01-08'
  },
  {
    id: 3,
    name: 'Participant Welcome Flow',
    description: 'Send welcome email and resources when new participant joins',
    trigger: 'Participant Added',
    actions: ['Send Welcome Email', 'Share Resources'],
    status: 'active',
    executions: 12,
    lastRun: '2024-01-15 11:30:00',
    created: '2024-01-05'
  },
  {
    id: 4,
    name: 'Idle Case Reminder',
    description: 'Remind owner if no activity for 7 days',
    trigger: 'Schedule (Daily)',
    actions: ['Send Reminder Email'],
    status: 'paused',
    executions: 8,
    lastRun: '2024-01-10 09:00:00',
    created: '2024-01-01'
  }
];

const triggerTypes = [
  { id: 'document_upload', label: 'Document Upload', icon: FiUpload },
  { id: 'document_approve', label: 'Document Approved', icon: FiCheck },
  { id: 'participant_add', label: 'Participant Added', icon: FiUser },
  { id: 'message_sent', label: 'Message Sent', icon: FiMessageSquare },
  { id: 'schedule', label: 'Schedule (Time-based)', icon: FiClock },
  { id: 'status_change', label: 'Status Change', icon: FiActivity }
];

const actionTypes = [
  { id: 'send_email', label: 'Send Email', icon: FiMail },
  { id: 'send_notification', label: 'In-App Notification', icon: FiAlertCircle },
  { id: 'update_status', label: 'Update Status', icon: FiRefreshCw },
  { id: 'assign_task', label: 'Assign Task', icon: FiCheck },
  { id: 'share_document', label: 'Share Document', icon: FiFileText },
  { id: 'webhook', label: 'Call Webhook', icon: FiZap }
];

const AutomationHub = ({ dealId }) => {
  const [automations, setAutomations] = useState(mockAutomations);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [newAutomation, setNewAutomation] = useState({
    name: '',
    description: '',
    trigger: 'document_upload',
    actions: [],
    conditions: []
  });

  const toggleAutomation = (id) => {
    setAutomations(automations.map(auto =>
      auto.id === id
        ? { ...auto, status: auto.status === 'active' ? 'paused' : 'active' }
        : auto
    ));
  };

  const deleteAutomation = (id) => {
    if (confirm('Are you sure you want to delete this automation?')) {
      setAutomations(automations.filter(auto => auto.id !== id));
    }
  };

  const addAction = (actionId) => {
    if (!newAutomation.actions.includes(actionId)) {
      setNewAutomation({
        ...newAutomation,
        actions: [...newAutomation.actions, actionId]
      });
    }
  };

  const removeAction = (actionId) => {
    setNewAutomation({
      ...newAutomation,
      actions: newAutomation.actions.filter(a => a !== actionId)
    });
  };

  const createAutomation = () => {
    if (!newAutomation.name || newAutomation.actions.length === 0) {
      alert('Please provide a name and at least one action');
      return;
    }

    const automation = {
      id: automations.length + 1,
      name: newAutomation.name,
      description: newAutomation.description,
      trigger: triggerTypes.find(t => t.id === newAutomation.trigger)?.label || 'Document Upload',
      actions: newAutomation.actions.map(a => actionTypes.find(at => at.id === a)?.label).filter(Boolean),
      status: 'active',
      executions: 0,
      lastRun: 'Never',
      created: new Date().toISOString().split('T')[0]
    };

    setAutomations([...automations, automation]);
    setIsCreating(false);
    setNewAutomation({
      name: '',
      description: '',
      trigger: 'document_upload',
      actions: [],
      conditions: []
    });
  };

  const stats = {
    total: automations.length,
    active: automations.filter(a => a.status === 'active').length,
    totalExecutions: automations.reduce((sum, a) => sum + a.executions, 0),
    successRate: 98.5
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-sm border border-blue-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500 rounded-lg">
              <SafeIcon icon={FiZap} className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-blue-700">Total Automations</p>
              <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm border border-green-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500 rounded-lg">
              <SafeIcon icon={FiPlay} className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-green-700">Active</p>
              <p className="text-2xl font-bold text-green-900">{stats.active}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-sm border border-purple-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500 rounded-lg">
              <SafeIcon icon={FiActivity} className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-purple-700">Total Executions</p>
              <p className="text-2xl font-bold text-purple-900">{stats.totalExecutions}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm border border-green-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500 rounded-lg">
              <SafeIcon icon={FiTrendingUp} className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-green-700">Success Rate</p>
              <p className="text-2xl font-bold text-green-900">{stats.successRate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-800">Automation Rules</h3>
          <p className="text-sm text-gray-500 mt-1">Automate workflows and save time</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-5 rounded-lg shadow-sm transition-colors"
        >
          <SafeIcon icon={FiPlus} />
          Create Automation
        </motion.button>
      </div>

      {/* Automations List */}
      <div className="space-y-3">
        {automations.map((automation, index) => (
          <motion.div
            key={automation.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all overflow-hidden"
          >
            <div className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-lg font-bold text-gray-900">{automation.name}</h4>
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                      automation.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {automation.status === 'active' ? 'Active' : 'Paused'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{automation.description}</p>
                  
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <SafeIcon icon={FiZap} className="w-4 h-4 text-blue-500" />
                      <span className="font-medium">Trigger:</span>
                      <span>{automation.trigger}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <SafeIcon icon={FiActivity} className="w-4 h-4 text-purple-500" />
                      <span>{automation.executions} executions</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <SafeIcon icon={FiClock} className="w-4 h-4 text-gray-500" />
                      <span>Last run: {automation.lastRun}</span>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {automation.actions.map((action, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                        {action}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleAutomation(automation.id)}
                    className={`p-2 rounded-lg transition-colors ${
                      automation.status === 'active'
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-50'
                    }`}
                    title={automation.status === 'active' ? 'Pause' : 'Activate'}
                  >
                    <SafeIcon icon={automation.status === 'active' ? FiToggleRight : FiToggleLeft} className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => setSelectedAutomation(automation)}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <SafeIcon icon={FiEdit3} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => deleteAutomation(automation.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {automations.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <SafeIcon icon={FiZap} className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No automations yet</h3>
          <p className="text-gray-600 mb-6">Create your first automation to streamline your workflow</p>
          <button
            onClick={() => setIsCreating(true)}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-5 rounded-lg"
          >
            <SafeIcon icon={FiPlus} />
            Create Automation
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {isCreating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsCreating(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">Create Automation</h3>
                    <p className="text-sm text-gray-500 mt-1">Set up a new automation rule</p>
                  </div>
                  <button
                    onClick={() => setIsCreating(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Automation Name *
                    </label>
                    <input
                      type="text"
                      value={newAutomation.name}
                      onChange={(e) => setNewAutomation({ ...newAutomation, name: e.target.value })}
                      placeholder="e.g., Document Upload Notification"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={newAutomation.description}
                      onChange={(e) => setNewAutomation({ ...newAutomation, description: e.target.value })}
                      placeholder="Describe what this automation does..."
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Trigger */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    When this happens (Trigger) *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {triggerTypes.map(trigger => (
                      <button
                        key={trigger.id}
                        onClick={() => setNewAutomation({ ...newAutomation, trigger: trigger.id })}
                        className={`flex items-center gap-3 p-4 border-2 rounded-lg transition-all ${
                          newAutomation.trigger === trigger.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <SafeIcon icon={trigger.icon} className={`w-5 h-5 ${
                          newAutomation.trigger === trigger.id ? 'text-blue-600' : 'text-gray-600'
                        }`} />
                        <span className={`font-medium ${
                          newAutomation.trigger === trigger.id ? 'text-blue-900' : 'text-gray-700'
                        }`}>
                          {trigger.label}
                        </span>
                        {newAutomation.trigger === trigger.id && (
                          <SafeIcon icon={FiCheck} className="ml-auto w-5 h-5 text-blue-600" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Do these actions *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {actionTypes.map(action => (
                      <button
                        key={action.id}
                        onClick={() => {
                          if (newAutomation.actions.includes(action.id)) {
                            removeAction(action.id);
                          } else {
                            addAction(action.id);
                          }
                        }}
                        className={`flex items-center gap-3 p-4 border-2 rounded-lg transition-all ${
                          newAutomation.actions.includes(action.id)
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <SafeIcon icon={action.icon} className={`w-5 h-5 ${
                          newAutomation.actions.includes(action.id) ? 'text-green-600' : 'text-gray-600'
                        }`} />
                        <span className={`font-medium flex-1 text-left ${
                          newAutomation.actions.includes(action.id) ? 'text-green-900' : 'text-gray-700'
                        }`}>
                          {action.label}
                        </span>
                        {newAutomation.actions.includes(action.id) && (
                          <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Info Banner */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                  <SafeIcon icon={FiShield} className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-blue-900">HIPAA Compliant</p>
                    <p className="text-xs text-blue-700 mt-1">
                      All automations are logged and comply with HIPAA regulations. Sensitive data is encrypted.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0">
                <button
                  onClick={() => setIsCreating(false)}
                  className="py-2.5 px-5 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={createAutomation}
                  disabled={!newAutomation.name || newAutomation.actions.length === 0}
                  className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2.5 px-6 rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
                >
                  <SafeIcon icon={FiZap} />
                  Create Automation
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AutomationHub;